"use strict";
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _cacheImageFile, _cacheImageContent, _supportedFilters;
Object.defineProperty(exports, "__esModule", { value: true });
exports.VideoFiltersHelper = void 0;
const logging_1 = __importDefault(require("../../lib/logging"));
const WebRtcUtils_1 = require("../../lib/WebRtcUtils");
const Exceptions_1 = require("../../lib/Exceptions");
const VideoFilters_1 = require("../../models/VideoFilters");
/**
 * @ignore
 */
const EMPTY_IMAGE_CONTENT = '';
/**
 * @ignore
 * Internal class.
 * Class sets video filter on a video track.
 */
class VideoFiltersHelper {
    constructor() {
        _cacheImageFile.set(this, null);
        _cacheImageContent.set(this, EMPTY_IMAGE_CONTENT);
        // Do not set a default value for #supportedFilters.
        // It's lazy initialized in getter.
        // Do no use #supportedFilters directly. Use it's getter instead.
        _supportedFilters.set(this, void 0);
        this.filterHandlers = new Map([
            [VideoFilters_1.VideoFilter.None, VideoFiltersHelper.prototype.filterNoneHandler],
            [VideoFilters_1.VideoFilter.Bokeh, VideoFiltersHelper.prototype.filterBokehHandler],
            [VideoFilters_1.VideoFilter.StaticImage, VideoFiltersHelper.prototype.filterStaticImageHandler],
        ]);
    }
    get supportedFilters() {
        if (!__classPrivateFieldGet(this, _supportedFilters)) {
            try {
                // Typescript disallows to get supported constrains which are not
                // part of specification. Delegate this to JavaScript.
                if (WebRtcUtils_1.isVideoFiltersSupported()) {
                    __classPrivateFieldSet(this, _supportedFilters, [VideoFilters_1.VideoFilter.None, VideoFilters_1.VideoFilter.Bokeh, VideoFilters_1.VideoFilter.StaticImage]);
                }
                else {
                    __classPrivateFieldSet(this, _supportedFilters, []);
                }
            }
            catch (e) {
                __classPrivateFieldSet(this, _supportedFilters, []);
                logging_1.default.warn('Checking system capabilities failed. ' + e);
            }
        }
        return __classPrivateFieldGet(this, _supportedFilters);
    }
    isFilterSupported(filter) {
        return this.supportedFilters.includes(filter);
    }
    setFilter(stream, filter, options = {}) {
        return new Promise((resolve, reject) => {
            if (!this.isFilterSupported(filter)) {
                reject(new Exceptions_1.UnsupportedError("Setting video filter failed. Filter '" + filter + "' is unsupported."));
                return;
            }
            if (!stream) {
                reject(new Error('Setting video filter failed. Stream is null'));
                return;
            }
            if (stream instanceof MediaStream)
                resolve(this.setFilterOnStream(stream, filter, options));
            else
                resolve(this.setFilterOnTrack(stream, filter, options));
        });
    }
    setFilterOnStream(stream, filter, options = {}) {
        return new Promise((resolve, reject) => {
            if (stream.getVideoTracks().length > 0 && stream.getVideoTracks()[0] != null)
                resolve(this.setFilterOnTrack(stream.getVideoTracks()[0], filter, options));
            else
                reject(new Error('Setting video filter failed. There is no video tracks in stream.'));
        });
    }
    setFilterOnTrack(track, filter, options = {}) {
        return new Promise((resolve, reject) => {
            if (track.kind === 'video')
                resolve(this.handleFilter(track, filter, options));
            else
                reject(new Error("Setting video filter failed. Media track isn't a video track"));
        });
    }
    handleFilter(track, filter, options) {
        return new Promise((resolve, reject) => {
            const filterHandler = this.filterHandlers.get(filter);
            if (filterHandler)
                resolve(filterHandler.call(this, track, options));
            else
                reject(new Error('Filter handler is undefined.'));
        }).then(() => logging_1.default.debug(`Set video filter '${filter}' on video track '${track.id}'`));
    }
    filterNoneHandler(track, options) {
        return options.videoDenoise
            ? // Set an empty filter which does not change participant's background
                // but allows to enable a 'video denoise' feature.
                VideoFiltersHelper.applyEffect(track, { mode: 'none', denoise: true })
            : // Disables a whole video filter processing block to reduce CPU usage.
                // In this mode 'video denoise' feature is unavailable.
                VideoFiltersHelper.applyEffect(track, { mode: 'disabled' });
    }
    filterBokehHandler(track, options) {
        return VideoFiltersHelper.applyEffect(track, { mode: 'bokeh', denoise: options.videoDenoise });
    }
    filterStaticImageHandler(track, options) {
        return this.readFileAsBinaryString(options.imageFile).then((imageData) => VideoFiltersHelper.applyEffect(track, { mode: 'static_image', imageData: imageData, denoise: options.videoDenoise }));
    }
    static applyEffect(track, options) {
        // Typescript disallows to apply video constraints which are not
        // part of specification. Delegate this to JavaScript.
        return WebRtcUtils_1.applyVideoFilterConstraints(track, options.mode, options.imageData || EMPTY_IMAGE_CONTENT, options.denoise || false);
    }
    readFileAsBinaryString(file) {
        return new Promise((resolve, reject) => {
            if (!file) {
                reject(new Error('Reading image file failed. File is null.'));
                return;
            }
            // don't read a file content twice, but return data from cache
            if (__classPrivateFieldGet(this, _cacheImageFile) === file) {
                resolve(__classPrivateFieldGet(this, _cacheImageContent));
                return;
            }
            const reader = new FileReader();
            reader.onerror = () => {
                reader.abort();
                reject(new Error('Reading image file failed. ' + reader.error));
            };
            reader.onload = () => {
                __classPrivateFieldSet(this, _cacheImageFile, file);
                __classPrivateFieldSet(this, _cacheImageContent, reader.result);
                resolve(reader.result);
            };
            reader.readAsBinaryString(file);
        });
    }
}
exports.VideoFiltersHelper = VideoFiltersHelper;
_cacheImageFile = new WeakMap(), _cacheImageContent = new WeakMap(), _supportedFilters = new WeakMap();
